import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import { AUTHOR } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Blog() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    axios.get(`${API}/posts`).then((res) => setPosts(res.data)).catch(() => setPosts([]));
  }, []);

  return (
    <main className="bg-white" data-testid="blog-page">
      <Helmet>
        <title>AI Voice Guides & Reviews | VoiceRanker.ai Blog</title>
        <meta name="description" content="Independent editorial guides, reviews and comparisons of AI voice generators — ElevenLabs, Murf, PlayHT and more." />
        <meta property="og:title" content="AI Voice Guides & Reviews | VoiceRanker.ai Blog" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://voiceranker.ai/blog" />
        <link rel="canonical" href="https://voiceranker.ai/blog" />
      </Helmet>
      <div className="bg-[#F8FAFC] border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">The VoiceRanker Blog</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight text-[#0F172A]" data-testid="blog-title">
            AI Voice Guides & Reviews
          </h1>
          <p className="mt-4 text-base md:text-lg text-slate-600 max-w-2xl leading-relaxed">
            Independent editorial comparisons, honest recommendations, and practical tutorials — everything you need to choose and use AI voice tools in 2026.
          </p>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {posts === null ? (
          <p className="text-slate-500" data-testid="blog-loading">Loading articles…</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {posts.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="group border border-slate-200 rounded-lg overflow-hidden hover:border-[#1A56DB] hover:shadow-md transition-all duration-200 flex flex-col"
              >
                {p.has_image && (
                  <img src={`${API}/posts/${p.slug}/image`} alt={p.title} loading="lazy" data-testid={`blog-card-image-${p.slug}`} className="w-full h-44 object-cover" />
                )}
                <div className="p-8 flex flex-col flex-1">
                <div className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.15em]">
                  <span className="text-[#5A31F4]">{p.category}</span>
                  <span className="text-slate-300">•</span>
                  <span className="text-slate-500">{p.read_time}</span>
                </div>
                <h2 className="mt-4 font-heading text-xl sm:text-2xl font-bold text-[#0F172A] group-hover:text-[#1A56DB] transition-colors leading-snug">
                  {p.title}
                </h2>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">{p.excerpt}</p>
                <div className="mt-6 flex items-center justify-between">
                  <span className="text-xs text-slate-500">{AUTHOR}</span>
                  <span className="inline-flex items-center gap-1.5 text-sm font-bold text-[#1A56DB]">
                    Read article <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                  </span>
                </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
